import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Users, CheckCircle2, XCircle, Clock4 } from "lucide-react";
import { toast } from "sonner";
import { markAttendanceSmart, getQueue, isOnline } from "@/lib/offline";

const todayISO = () => new Date().toISOString().slice(0, 10);
const STATUSES = [
  { v: "present", short: "P", color: "bg-emerald-600 text-white", off: "text-emerald-700" },
  { v: "late",    short: "L", color: "bg-amber-500 text-white",   off: "text-amber-700" },
  { v: "absent",  short: "A", color: "bg-red-600 text-white",     off: "text-red-700" },
];

const MobileClass = () => {
  const [students, setStudents] = useState([]);
  const [recs, setRecs] = useState({});
  const [grade, setGrade] = useState("all");
  const [loading, setLoading] = useState(false);
  const date = todayISO();

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/students"),
        api.get("/attendance", { params: { date, subject_type: "student" } }),
      ]);
      setStudents(s.data);
      const m = {}; a.data.forEach((r) => { m[r.subject_id] = r; });
      setRecs(m);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const grades = useMemo(() => Array.from(new Set(students.map((s) => s.grade))).sort(), [students]);
  const filtered = grade === "all" ? students : students.filter((s) => s.grade === grade);

  const mark = async (stu, status) => {
    const res = await markAttendanceSmart({ subject_id: stu.id, subject_type: "student", date, status, method: "manual" });
    if (res.data) {
      setRecs((m) => ({ ...m, [stu.id]: res.data }));
    } else {
      // queued offline → optimistic
      setRecs((m) => ({ ...m, [stu.id]: { subject_id: stu.id, status, method: "manual", _queued: true } }));
    }
    navigator.vibrate?.(15);
  };

  const summary = filtered.reduce((acc, s) => { const r = recs[s.id]; if (r) acc[r.status] = (acc[r.status] || 0) + 1; else acc.unmarked++; return acc; }, { present:0, absent:0, late:0, excused:0, unmarked:0 });

  return (
    <div className="px-4 pt-4 pb-6" data-testid="mobile-class-page">
      <h2 className="font-display text-xl text-[#1B2C5A] flex items-center gap-2"><Users className="w-5 h-5 text-[#9DCD3F]" /> Class Attendance</h2>
      <p className="text-xs text-[#475569]">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>

      {/* Grade pills */}
      <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-thin -mx-4 px-4 pb-1" data-testid="grade-filter">
        {["all", ...grades].map((g) => (
          <button
            key={g}
            onClick={() => setGrade(g)}
            className={"shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap " + (grade === g ? "bg-[#1B2C5A] text-white" : "bg-white text-[#475569] border border-[#E2E8F0]")}
            data-testid={`grade-${g}`}
          >{g === "all" ? "All Classes" : g}</button>
        ))}
      </div>

      {/* Summary chips */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
        <Chip label="P" value={summary.present || 0} className="bg-emerald-50 text-emerald-700" />
        <Chip label="L" value={summary.late || 0} className="bg-amber-50 text-amber-700" />
        <Chip label="A" value={summary.absent || 0} className="bg-red-50 text-red-700" />
        <Chip label="—" value={summary.unmarked || 0} className="bg-slate-50 text-slate-700" />
      </div>

      {/* Roster */}
      {loading ? <div className="text-center text-[#475569] py-8 text-sm">Loading…</div> : (
        <ul className="mt-4 space-y-2" data-testid="roster">
          {filtered.map((s) => {
            const r = recs[s.id];
            return (
              <li key={s.id} className="p-3 rounded-xl bg-white border border-[#E2E8F0] flex items-center gap-3" data-testid={`roster-${s.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#1B2C5A]/5 text-[#1B2C5A] grid place-items-center text-xs font-medium">
                  {s.full_name.split(" ").slice(0, 2).map((p) => p[0]).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1B2C5A] truncate">{s.full_name}</div>
                  <div className="text-xs text-[#475569]">{s.grade} · {s.admission_no}</div>
                </div>
                <div className="flex gap-1.5">
                  {STATUSES.map((st) => (
                    <button
                      key={st.v}
                      onClick={() => mark(s, st.v)}
                      className={"w-9 h-9 rounded-full text-xs font-bold transition " + (r?.status === st.v ? st.color : "bg-white border border-[#E2E8F0] " + st.off)}
                      data-testid={`mark-${s.id}-${st.v}`}
                    >
                      {st.short}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const Chip = ({ label, value, className }) => (
  <div className={"py-2 rounded-lg " + className}>
    <div className="font-bold">{value}</div>
    <div className="opacity-70">{label}</div>
  </div>
);

export default MobileClass;
